// src/pages/agent/KnowledgeBase.js
// Glossary of wholesale real estate terms — quick reference for everyone
// (admins, agents, and lead submitters) inside the portal.
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { PageHeader, Card, Btn, Input, BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, MID, LIGHT, BORDER } from '../../components/agent/UI';

const TERMS = [
  // ── Core wholesaling vocabulary
  { term: 'ARV (After Repair Value)',  def: 'The estimated value of a property after all repairs are made.' },
  { term: 'Assignment Fee',            def: 'The profit you make by selling your contract to a buyer.' },
  { term: 'FSBO (For Sale By Owner)',  def: 'A property being sold directly by the owner, without a real estate agent.' },
  { term: 'Repairs',                   def: 'The estimated cost to fix up a property.' },
  { term: 'Wholesale Deal',            def: 'Getting a property under contract and selling the contract to another buyer.' },
  { term: 'Distressed Seller',         def: 'A seller who needs to sell quickly, often due to financial or personal reasons.' },
  { term: 'Buyer’s Price',             def: 'The maximum price an investor is willing to pay for a property.' },
  { term: 'Motivation',                def: 'The reason a seller wants to sell the property.' },
  { term: 'Lead',                      def: 'A potential seller or property contact.' },
  { term: 'Follow-up',                 def: 'Contacting leads repeatedly to move the deal forward. Most deals close on the second, third, or fourth touch — not the first.' },

  // ── The numbers
  { term: 'MAO (Maximum Allowable Offer)', def: 'The highest price you should pay. Formula: ARV × 70% − Repairs − Assignment Fee.' },
  { term: 'The 70% Rule',              def: 'The industry-standard ceiling: don\'t pay more than 70% of ARV (minus repairs and assignment fee). Used to leave room for a cash buyer\'s margin.' },
  { term: 'Comps (Comparables)',       def: 'Recently sold houses similar to the subject property — used to estimate ARV. Aim for 3+ within 1 mile, sold within the last 6 months.' },
  { term: 'EMD (Earnest Money Deposit)', def: 'Money the buyer puts down on signing to lock the contract. In wholesaling, often non-refundable and applied to the purchase price at closing.' },
  { term: 'HUD-1 / Closing Statement', def: 'The settlement document showing every dollar in and out of the closing — including the assignment fee and any administrative fees.' },

  // ── Property types / lead conditions
  { term: 'Vacant House',              def: 'A house no one currently lives in — often a great wholesale target because owner motivation is usually high.' },
  { term: 'Tired Landlord',            def: 'A property owner who\'s done dealing with tenants — typically very motivated to offload the rental.' },
  { term: 'Inherited House',           def: 'A property received via inheritance. The new owner often has emotional distance and wants a fast cash-out.' },
  { term: 'Outdated Home',             def: 'A property that hasn\'t been updated in decades — typically distressed and a candidate for wholesaling.' },

  // ── 4 Pillars + lead temperature (from your Pillars doc)
  { term: '4 Pillars of Motivation',   def: 'The four things to confirm on every seller call: (1) Motivation — why selling, (2) Timeline — how soon, (3) Condition — what repairs, (4) Price — what they hope to get.' },
  { term: 'Cold Lead (0–3 pts)',       def: 'No real reason to sell, no urgency, retail-priced ask, and the property is in good condition. Low priority.' },
  { term: 'Warm Lead (4–5 pts)',       def: 'Some motivation, ~3 month timeline, asking 10–30% below Zillow, property needs minor work. Worth a follow-up cadence.' },
  { term: 'Hot Lead (6–10 pts)',       def: 'Must sell, definite timeline (≤1 month), asking 30%+ below Zillow, significant repairs needed. Top priority — call today.' },

  // ── Operations
  { term: 'Skip Tracing',              def: 'Finding a current phone or email for a property owner who isn\'t directly listed. Critical for off-market lead lists.' },
  { term: 'Cold Call',                 def: 'An unsolicited first contact with a property owner — usually FSBO listings. Goal: qualify against the 4 Pillars.' },
  { term: 'Renegotiation',             def: 'Reopening price after inspection or condition issues surface — done before closing.' },

  // ── Contracts & parties
  { term: 'Assignor',                  def: 'The wholesaler — the original buyer who has the property under contract and is selling that contract position.' },
  { term: 'Assignee',                  def: 'The end buyer — usually a cash investor — who takes over the wholesaler\'s contract.' },
  { term: 'Novation',                  def: 'Replacing the original buyer with a new one in a contract. Also: the limited POA that lets a wholesaler list the property on the seller\'s behalf.' },
  { term: 'JV Partner',                def: 'A joint-venture partner. Another wholesaler or agent who splits the assignment fee with you on a specific deal.' },
  { term: 'Title Company',             def: 'Third-party that handles title search, escrow, and closing paperwork. They issue the title policy guaranteeing clean ownership.' },
  { term: 'Marketable Title',          def: 'Clean ownership free of liens, judgments, or encumbrances. The seller is required to deliver this at closing.' },
  { term: 'Closing',                   def: 'The final transaction where ownership transfers, money changes hands, and the deed is recorded.' },

  // ── Daily rules
  { term: 'Daily Rule for Success',    def: 'Make 2–3 offers per day. Not per week. More offers = more deals. Volume is the engine.' },
  { term: 'Wholesale Formula',         def: 'ARV × 70% − Repairs − Assignment Fee = MAO. Offer slightly lower for negotiation room.' },

  // ── Owner research
  {
    term: 'LLC-Owned Property Lookup',
    def: 'When a property is owned by a company (LLC, trust, etc.), find the real human behind it before you call. Three free tools to identify the owner: (1) OpenCorporates — opencorporates.com — global LLC registry. Search the LLC name to find filing state, registered agent, and listed members/managers. (2) Bizapedia — bizapedia.com — US business records. Often shows the principal name + address right on the company page. (3) CyberBackgroundChecks — cyberbackgroundchecks.com — once you have the principal\'s name, search them here for current phone numbers, emails, relatives, and address history. Workflow: county tax records → LLC name → OpenCorporates/Bizapedia → principal name → CyberBackgroundChecks → cell number.'
  }
];

export default function KnowledgeBase() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [aiAnswer, setAiAnswer] = useState(null); // { answer, action }
  const [aiError, setAiError] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return TERMS;
    return TERMS.filter(t =>
      t.term.toLowerCase().includes(q) || t.def.toLowerCase().includes(q)
    );
  }, [search]);

  const ask = async (e) => {
    e?.preventDefault?.();
    if (!question.trim()) return;
    setAsking(true); setAiError(''); setAiAnswer(null);
    try {
      const fn = httpsCallable(functions, 'kbAssist');
      const res = await fn({ question: question.trim() });
      if (!res.data?.ok) throw new Error('No answer returned.');
      setAiAnswer({ answer: res.data.answer, action: res.data.action });
    } catch (e) {
      setAiError(`Couldn't answer: ${e.message}`);
    } finally {
      setAsking(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Knowledge base"
        title={<>Wholesale real estate <em style={{ fontStyle: 'italic', color: BURG }}>terms</em>.</>}
        sub="Ask the AI a question, search the glossary, or scroll the full list. Powered by your team's terminology and HomeIQa's tools."
      />

      {/* AI assistant */}
      <Card title="Ask the AI" right={<span style={{ fontSize: 11, color: LIGHT, fontStyle: 'italic' }}>Try: "How do I calculate ARV?" · "What's the 70% rule?"</span>}>
        <form onSubmit={ask} style={ai.form}>
          <Input
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="Ask a wholesaling question or how to do something in HomeIQa…"
            style={{ flex: 1 }}
            disabled={asking}
          />
          <Btn variant="gold" type="submit" disabled={asking || !question.trim()}>
            {asking ? 'Thinking…' : '✨ Ask'}
          </Btn>
        </form>
        {aiError && <div style={ai.err}>{aiError}</div>}
        {aiAnswer && (
          <div style={ai.answerBox}>
            <div style={ai.answerLabel}>Answer</div>
            <div style={ai.answerText}>{aiAnswer.answer}</div>
            {aiAnswer.action?.route && (
              <div style={{ marginTop: 14 }}>
                <Btn variant="gold" onClick={() => navigate(aiAnswer.action.route)}>
                  {aiAnswer.action.label || 'Open in HomeIQa'} →
                </Btn>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card padded={false}>
        <div style={s.searchBar}>
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search terms — e.g. ARV, distressed, follow-up..."
            style={{ width: '100%' }}
          />
          <div style={s.count}>{filtered.length} of {TERMS.length}</div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: LIGHT, fontSize: 13 }}>
            No terms match "<em>{search}</em>". Try a shorter keyword.
          </div>
        ) : (
          <ul style={s.list}>
            {filtered.map((t, i) => {
              // Find the original index from TERMS so the number stays stable when filtered
              const originalIndex = TERMS.findIndex(x => x.term === t.term) + 1;
              return (
                <li key={t.term} style={s.item}>
                  <div style={s.num}>{originalIndex}</div>
                  <div style={s.body}>
                    <div style={s.term}>{t.term}</div>
                    <div style={s.def}>{t.def}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card title="Want to add more terms?">
        <div style={{ fontSize: 13, color: MID, lineHeight: 1.65 }}>
          Tell me what to add — anything from <strong>MAO</strong>, <strong>EMD</strong>, and <strong>title contingency</strong> to <strong>novation</strong>, <strong>JV split</strong>, <strong>1031 exchange</strong>, or your team's house terminology. The glossary is a single file, easy to grow as your playbook does.
        </div>
      </Card>
    </>
  );
}

const ai = {
  form: { display: 'flex', gap: 10, alignItems: 'center' },
  err: { padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, color: '#991B1B', fontSize: 12, marginTop: 12 },
  answerBox: { marginTop: 16, padding: '18px 20px', background: GOLD_PALE, borderLeft: `3px solid ${GOLD}`, borderRadius: '0 6px 6px 0' },
  answerLabel: { fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, marginBottom: 8 },
  answerText: { fontSize: 14, color: BURG_DK, lineHeight: 1.65 }
};

const s = {
  searchBar: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: `1px solid ${BORDER}`, background: CREAM },
  count: { fontSize: 11, color: LIGHT, fontStyle: 'italic', whiteSpace: 'nowrap', letterSpacing: '0.04em' },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  item: { display: 'flex', alignItems: 'flex-start', gap: 18, padding: '20px 22px', borderBottom: `1px solid ${BORDER}`, transition: 'background 0.15s ease' },
  num: { fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 600, color: GOLD, lineHeight: 1, minWidth: 38, textAlign: 'center', paddingTop: 2, letterSpacing: '-1px' },
  body: { flex: 1, minWidth: 0 },
  term: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 500, color: BURG_DK, marginBottom: 4, letterSpacing: '-0.2px' },
  def: { fontSize: 14, color: MID, lineHeight: 1.65 }
};
